import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { CameraPatternLinkContext, LinkedCriticalRiskPattern } from '../../domain/overview/cameraPatternContext';
import {
  ANNOTATED_OVERLAY_UNAVAILABLE,
  LIVE_VIEW_PATTERN_CARD_TITLE,
  OPEN_LINKED_EVENT_RECORDS_LABEL,
  OPEN_LINKED_EVENT_RECORDS_TOOLTIP,
  buildViewEventRecordsPath,
  liveViewPatternDisplayName,
  liveViewPatternPreviewLine,
  liveViewPatternRuleSecondary,
  liveViewPatternSectionTitle,
  operatorFacingAreaLabel,
} from '../../domain/liveView/liveViewPatternDisplay';
import { displaySafetyManagerLabel } from '../../domain/safetyManagerUxLabels';
import { buildScopedIncidentsPath } from '../../utils/incidentsNavigation';
import type { Camera } from '../../services/camerasApi';

type LiveViewCameraPatternPanelProps = {
  camera: Camera;
  patternLink: CameraPatternLinkContext | null;
  onNavigate: (path: string) => void;
  viewKind?: 'live' | 'annotated';
  annotatedOverlayAvailable?: boolean;
  showTechnicalIds?: boolean;
};

function controlStatusChipColor(
  label: string,
): 'default' | 'error' | 'warning' | 'success' | 'info' {
  const key = label.toLowerCase();
  if (key.includes('not verified') || key.includes('needs review') || key === 'exposed') {
    return 'error';
  }
  if (key.includes('owner assigned') || key.includes('pending') || key.includes('follow')) {
    return 'warning';
  }
  if (key.includes('controlled')) return 'success';
  return 'default';
}

function OpenLinkedRecordsLine({ count }: { count: number }) {
  return (
    <Stack direction="row" spacing={0.35} alignItems="center" sx={{ mt: 0.25 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {OPEN_LINKED_EVENT_RECORDS_LABEL}: {count.toLocaleString()}
      </Typography>
      <Tooltip title={OPEN_LINKED_EVENT_RECORDS_TOOLTIP} arrow>
        <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.85 }} />
      </Tooltip>
    </Stack>
  );
}

function PatternCardRow({
  pattern,
  onNavigate,
  compact = false,
  showTechnicalIds = false,
}: {
  pattern: LinkedCriticalRiskPattern;
  onNavigate: (path: string) => void;
  compact?: boolean;
  showTechnicalIds?: boolean;
}) {
  const eventRecordsPath = buildViewEventRecordsPath(pattern.linkToPatternReview);
  const conditionName = liveViewPatternDisplayName(
    pattern.displayName || pattern.patternLabel,
    pattern.incidentType,
  );
  const areaLabel = operatorFacingAreaLabel(
    pattern.patternLabel,
    pattern.groupKey,
    pattern.zoneLabel,
  );

  return (
    <Box
      sx={{
        p: compact ? 1 : 1.15,
        borderRadius: 1.25,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
      data-testid="live-view-pattern-card-row"
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.3 }} data-testid="live-view-pattern-name">
        {conditionName}
      </Typography>
      {showTechnicalIds ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}>
          {liveViewPatternRuleSecondary(
            pattern.incidentType,
            pattern.patternLabel,
            pattern.groupKey,
            pattern.zoneLabel,
          )}
        </Typography>
      ) : null}

      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
        <Chip
          size="small"
          label={pattern.controlStatusLabel}
          color={controlStatusChipColor(pattern.controlStatusLabel)}
          variant="outlined"
          data-testid="live-view-pattern-control-status"
        />
        {areaLabel ? (
          <Typography variant="caption" color="text.secondary">
            Area: {areaLabel}
          </Typography>
        ) : null}
      </Stack>

      {!compact ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {displaySafetyManagerLabel('pattern_owner')}: {pattern.patternOwner}
        </Typography>
      ) : null}

      <OpenLinkedRecordsLine count={pattern.openLinkedEventRecords} />

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => onNavigate(buildScopedIncidentsPath(pattern.linkToPatternReview))}
        >
          Review recurring risk
        </Button>
        {eventRecordsPath ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => onNavigate(buildScopedIncidentsPath(eventRecordsPath))}
          >
            View event records
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
}

export function LiveViewCameraPatternPanel({
  camera,
  patternLink,
  onNavigate,
  viewKind = 'live',
  annotatedOverlayAvailable = false,
  showTechnicalIds = false,
}: LiveViewCameraPatternPanelProps) {
  const patterns = patternLink?.patterns || [];
  const [expanded, setExpanded] = useState(true);
  if (patterns.length === 0) return null;

  const title = LIVE_VIEW_PATTERN_CARD_TITLE;
  const headerLabel = liveViewPatternSectionTitle(patterns.length);
  const previewLine = liveViewPatternPreviewLine(patterns);
  const toggleLabel = expanded ? `Collapse ${title}` : `Expand ${title}`;
  const cardsId = `live-view-pattern-cards-${camera.camera_id}`;

  return (
    <Box
      sx={{
        mt: 1.25,
        pt: 1.25,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
      data-testid="live-view-camera-pattern-panel"
    >
      <Button
        variant="text"
        color="inherit"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        aria-controls={cardsId}
        aria-label={toggleLabel}
        endIcon={expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        data-testid="live-view-pattern-card-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
          px: 0,
          py: 0,
          mb: 0,
          color: 'text.primary',
          fontWeight: 800,
          fontSize: '0.875rem',
          lineHeight: 1.35,
          textTransform: 'none',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {headerLabel}
      </Button>
      {previewLine ? (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          data-testid="live-view-pattern-preview"
          sx={{ mt: expanded ? 0 : 0.15, mb: expanded ? 0.75 : 0, lineHeight: 1.35 }}
        >
          {previewLine}
        </Typography>
      ) : null}

      {expanded ? (
        <Box id={cardsId} data-testid="live-view-pattern-card-list">
          <Stack spacing={0.85}>
            {patterns.length === 1 ? (
              <PatternCardRow
                pattern={patterns[0]}
                onNavigate={onNavigate}
                showTechnicalIds={showTechnicalIds}
              />
            ) : (
              patterns.map((pattern) => (
                <PatternCardRow
                  key={pattern.linkToPatternReview}
                  pattern={pattern}
                  onNavigate={onNavigate}
                  compact
                  showTechnicalIds={showTechnicalIds}
                />
              ))
            )}
          </Stack>

          {viewKind === 'annotated' ? (
            <Box sx={{ mt: 0.85 }}>
              {annotatedOverlayAvailable && patterns[0] ? (
                <Stack spacing={0.25}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }} data-testid="live-view-annotated-condition-name">
                    {liveViewPatternDisplayName(
                      patterns[0].displayName || patterns[0].patternLabel,
                      patterns[0].incidentType,
                    )}
                  </Typography>
                  {operatorFacingAreaLabel(
                    patterns[0].patternLabel,
                    patterns[0].groupKey,
                    patterns[0].zoneLabel,
                  ) ? (
                    <Typography variant="caption" color="text.secondary">
                      Area: {operatorFacingAreaLabel(
                        patterns[0].patternLabel,
                        patterns[0].groupKey,
                        patterns[0].zoneLabel,
                      )}
                    </Typography>
                  ) : null}
                  {showTechnicalIds ? (
                    <Typography variant="caption" color="text.secondary">
                      {liveViewPatternRuleSecondary(
                        patterns[0].incidentType,
                        patterns[0].patternLabel,
                        patterns[0].groupKey,
                        patterns[0].zoneLabel,
                      )}
                    </Typography>
                  ) : null}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary" data-testid="live-view-annotated-overlay-unavailable">
                  {ANNOTATED_OVERLAY_UNAVAILABLE}
                </Typography>
              )}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
